import type * as React from "react";

import { cn } from "@/web/lib/styling";

/**
 * Track + animated shuttle for an unknown-duration loading state.
 *
 * Visual: a track with a solid block that slides from left to right on a loop,
 * entering with its right edge at the track's left edge and exiting with its
 * left edge at the track's right edge. Use for jobs where the backend doesn't
 * expose a percent complete (e.g. "kicking off pricing", "building your
 * underwriting questionnaire").
 *
 * The shuttle color follows the current `color` of the element, so callers
 * control the shuttle tint by passing a `text-*` class (e.g. `text-white`,
 * `text-primary`) to `indicatorClassName`. The track color follows the
 * element's `background-color`, set by `className`.
 *
 * For known-percent loading, prefer `<Progress />` from
 * `apps/web/src/components/primitives/progress.tsx`.
 *
 * @example
 *   <IndeterminateProgress
 *     className="bg-white/20"
 *     indicatorClassName="text-white"
 *   />
 *   <p className="text-xs opacity-70">This card will update as soon as it's ready.</p>
 */
function IndeterminateProgress({
  className,
  indicatorClassName,
  ...props
}: React.ComponentProps<"div"> & { indicatorClassName?: string }) {
  return (
    <div
      role="progressbar"
      aria-label="Loading"
      data-slot="indeterminate-progress"
      className={cn("relative h-1.5 w-full overflow-hidden rounded-none bg-primary/20", className)}
      {...props}
    >
      <div
        data-slot="indeterminate-progress-indicator"
        className={cn(
          "animate-indeterminate-progress absolute inset-y-0 left-0 w-1/2 bg-current",
          indicatorClassName,
        )}
      />
    </div>
  );
}

export { IndeterminateProgress };
